import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Button,
  Chip,
  Divider
} from '@mui/material';
import { ArrowForward, Article, School, Support } from '@mui/icons-material';
import { havenColors } from '../../theme/theme';

const ResourcesPage: React.FC = () => {
  const [showAllPosts, setShowAllPosts] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const allBlogPosts = [
    {
      title: 'No One CAREs About Residential Controls – An Open Letter',
      excerpt: 'Let\'s face it: No one CAREs about residential controls - and that needs to change. The current state of residential HVAC controls, especially when it comes to Indoor Air Quality (IAQ), is flat-out inadequate.',
      category: 'Controls',
      readTime: '5 min read',
      date: 'September 18, 2024',
      author: 'Kevin Hart',
      slug: 'no-one-cares-about-residential-controls',
      hasInternalPage: true
    },
    {
      title: 'HAVEN Meets TrickleStar: Wireless IAQ Integrations',
      excerpt: 'HAVEN\'s Central Air Monitor is now compatible with TrickleStar\'s Smart Thermostat! Our new wireless IAQ integrations help you maximize savings, optimize comfort, and breathe cleaner air.',
      category: 'Technology',
      readTime: '4 min read',
      date: 'October 17, 2022',
      author: 'Christina S',
      slug: 'haven-meets-tricklestar',
      hasInternalPage: true
    },
    {
      title: 'Health effects of Particulate Matter',
      excerpt: 'When the air that we breathe is compromised (eg. there are too many particles in the air), we can feel sluggish or tired. Fine particulate matter is particularly dangerous, as particles of 2.5UG or lower are small enough to enter directly into our organs!',
      category: 'Health & Safety',
      readTime: '6 min read',
      date: 'January 11, 2022',
      author: 'Lucy C',
      url: 'https://haveniaq.com/2022/01/health-effects-of-particulate-matter/',
      hasInternalPage: false
    },
    {
      title: 'Particulate Matter and Volatile Organic Compounds',
      excerpt: 'What causes poor indoor air quality? What is particulate matter? Where does VOC pollution come from? Learn how to improve your air quality at home today!',
      category: 'Education',
      readTime: '7 min read',
      date: 'November 4, 2021',
      author: 'Lucy C',
      url: 'https://haveniaq.com/2021/11/particulate-matter-and-volatile-organic-compounds/',
      hasInternalPage: false
    },
    {
      title: 'HAVEN™ For Wildfire Season',
      excerpt: 'The majority of wildfire smoke exposure occurs while we\'re inside, which is why we believe HVAC pros are best placed to address IAQ issues…',
      category: 'Climate',
      readTime: '5 min read',
      date: 'July 7, 2021',
      author: 'Christina S',
      url: 'https://haveniaq.com/2021/07/haven-for-hvac-wildfire-season/',
      hasInternalPage: false
    },
    {
      title: 'HAVEN™ For Wildfire Smoke',
      excerpt: 'The air in your home may not become visibly smoky, but if levels outside are high the chances are that wildfire smoke particles have made it inside. Find out how you can stop the smoke and #breathebetter…',
      category: 'Climate',
      readTime: '4 min read',
      date: 'June 24, 2021',
      author: 'Christina S',
      url: 'https://haveniaq.com/2021/06/haven-for-wildfire-smoke/',
      hasInternalPage: false
    },
    {
      title: 'HAVEN™ For Asthma Sufferers',
      excerpt: 'Asthma can be triggered by common factors within the home such as dust, mold, and pet dander. Take a look at the links between air pollution and asthma – the causes, culprits, and changes you can make within your home to reduce the chances of an attack…',
      category: 'Health & Safety',
      readTime: '6 min read',
      date: 'May 20, 2021',
      author: 'Christina S',
      url: 'https://haveniaq.com/2021/05/air-pollution-and-asthma/',
      hasInternalPage: false
    },
    {
      title: 'HAVEN IAQ + IFTTT: Our First Step on the Path to Automate Your Air Quality',
      excerpt: 'We\'re excited to announce that we\'ve recently released our HAVEN IAQ service on IFTTT. Through IFTTT, a HAVEN IAQ user can use the Central Air Monitor\'s Temperature, Relative Humidity, VOC, and PM2.5 readings as triggers automations.',
      category: 'Technology',
      readTime: '3 min read',
      date: 'December 8, 2020',
      author: 'Lucy C',
      url: 'https://haveniaq.com/2020/12/haven-iaq-ifttt-our-first-step-on-the-journey-to-automate-your-air-quality/',
      hasInternalPage: false
    }
  ];

  const filteredPosts = selectedCategory === 'All' 
    ? allBlogPosts 
    : allBlogPosts.filter(post => post.category === selectedCategory);
  
  const displayedPosts = showAllPosts ? filteredPosts : filteredPosts.slice(0, 6);

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    setShowAllPosts(false); // Reset to show initial posts when filtering
  };

  const handleLoadMore = () => {
    setShowAllPosts(true);
  };

  const handleReadArticle = (post: any) => {
    if (post.hasInternalPage) {
      window.location.href = `/blog/${post.slug}`;
    } else {
      window.open(post.url, '_blank');
    }
  };

  const supportResources = [
    {
      title: 'Installation Guides',
      description: 'Step-by-step guides for HAVEN system installation and setup.',
      icon: <School />,
      type: 'Documentation'
    },
    {
      title: 'Troubleshooting',
      description: 'Common issues and solutions for HAVEN system maintenance.',
      icon: <Support />,
      type: 'Support'
    },
    {
      title: 'User Manuals',
      description: 'Comprehensive user guides for all HAVEN products and features.',
      icon: <Article />,
      type: 'Documentation'
    }
  ];

  const categories = ['All', 'Controls', 'Technology', 'Health & Safety', 'Education', 'Climate'];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.darkTeal} 100%)`,
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 700, mb: 3 }}>
            Resources & Support
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.95, maxWidth: '600px', mx: 'auto' }}>
            Everything you need to know about indoor air quality, HAVEN products, and maintaining healthy air in your home.
          </Typography>
        </Container>
      </Box>

      {/* Quick Access Cards */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" sx={{ mb: 6, fontWeight: 700, color: havenColors.darkGray }}>
          Quick Access
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {supportResources.map((resource, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: `${havenColors.primaryTeal}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <Box sx={{ color: havenColors.primaryTeal, fontSize: '2rem' }}>
                    {resource.icon}
                  </Box>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: havenColors.darkGray }}>
                  {resource.title}
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  {resource.description}
                </Typography>
                
                <Chip 
                  label={resource.type} 
                  size="small" 
                  sx={{ 
                    backgroundColor: `${havenColors.primaryTeal}10`,
                    color: havenColors.primaryTeal 
                  }} 
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* Blog Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h3" align="center" sx={{ mb: 4, fontWeight: 700, color: havenColors.darkGray }}>
            Latest Articles & Insights
          </Typography>
          
          {/* Category Filter */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                variant={category === selectedCategory ? 'filled' : 'outlined'}
                onClick={() => handleCategoryFilter(category)}
                                  sx={{
                    backgroundColor: category === selectedCategory ? havenColors.primaryTeal : 'transparent',
                    color: category === selectedCategory ? 'white' : havenColors.primaryTeal,
                    borderColor: havenColors.primaryTeal,
                    '&:hover': {
                      backgroundColor: category === selectedCategory ? havenColors.darkTeal : `${havenColors.primaryTeal}10`
                    }
                  }}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={4}>
          {displayedPosts.map((post: any, index: number) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box
                  sx={{
                    height: 200,
                    background: `linear-gradient(135deg, ${havenColors.primaryTeal}20 0%, ${havenColors.primaryTeal}10 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Article sx={{ fontSize: '4rem', color: havenColors.primaryTeal, opacity: 0.3 }} />
                </Box>
                
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={post.category} 
                      size="small" 
                      sx={{ 
                        backgroundColor: `${havenColors.primaryTeal}10`,
                        color: havenColors.primaryTeal,
                        mb: 1
                      }} 
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      {post.date} • {post.readTime} • By {post.author}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: havenColors.darkGray }}>
                    {post.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', flexGrow: 1 }}>
                    {post.excerpt}
                  </Typography>
                  
                  <Button
                    variant="text"
                    endIcon={<ArrowForward />}
                    onClick={() => handleReadArticle(post)}
                    sx={{
                      color: havenColors.primaryTeal,
                      fontWeight: 600,
                      justifyContent: 'flex-start',
                      pl: 0
                    }}
                  >
                    Read Article
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Load More Button */}
        {!showAllPosts && filteredPosts.length > 6 && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleLoadMore}
              sx={{
                borderColor: havenColors.primaryTeal,
                color: havenColors.primaryTeal,
                px: 4,
                '&:hover': {
                  backgroundColor: `${havenColors.primaryTeal}10`,
                  borderColor: havenColors.primaryTeal
                }
              }}
            >
              Load More Articles ({filteredPosts.length - 6} more)
            </Button>
          </Box>
        )}
      </Container>

      {/* Help Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, color: havenColors.darkGray }}>
                Need Additional Support?
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Our support team is here to help with any questions about HAVEN products, 
                installation, or indoor air quality management. Get personalized assistance 
                from our experts.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: havenColors.primaryTeal,
                    '&:hover': { backgroundColor: havenColors.darkTeal }
                  }}
                >
                  Contact Support
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: havenColors.primaryTeal,
                    color: havenColors.primaryTeal
                  }}
                >
                  Schedule Consultation
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  p: 4,
                  borderRadius: 3,
                  boxShadow: 2
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: havenColors.darkGray }}>
                  Support Hours
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Phone Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday: 8:00 AM - 6:00 PM EST
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Email Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    24/7 - Response within 24 hours
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Emergency Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available for critical system issues
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ResourcesPage; 